import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  ArrowLeftIcon,
} from "@heroicons/react/24/outline";

const AlertDialog = ({
  type = "error",
  title = "Alert",
  message = "Something happened.",
  confirmText = "Confirm",
  cancelText = "Cancel",
  open = false,
  onClose = () => {},
  onConfirm = () => {},
}) => {
  const styles = {
    error: {
      icon: <ExclamationTriangleIcon className="size-6 text-red-400" />,
      iconBg: "bg-gray-600",
      buttonBg: "bg-red-400 hover:bg-red-500",
    },
    success: {
      icon: <CheckCircleIcon className="size-6 text-orange-400" />,
      iconBg: "bg-gray-600",
      buttonBg: "bg-orange-400 hover:bg-orange-500",
    },
  };

  const { icon, iconBg, buttonBg } = styles[type] || styles.error;

  return (
    <Dialog open={open} onClose={onClose} className="relative z-10">
      <DialogBackdrop className="fixed inset-0 bg-gray-600/75 transition-opacity" />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel className="relative transform overflow-hidden rounded-lg bg-gray-700 px-4 pb-4 pt-5 shadow-xl transition-all sm:max-w-lg sm:p-6 border border-gray-600">
            <div className="absolute right-0 top-0 pr-4 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md text-gray-400 hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <XMarkIcon className="size-5" />
              </button>
            </div>
            <div className="sm:flex sm:items-start">
              <div
                className={`mx-auto flex size-10 shrink-0 items-center justify-center rounded-full ${iconBg} sm:mx-0`}
              >
                {icon}
              </div>
              <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                <DialogTitle
                  as="h3"
                  className="text-lg font-semibold text-orange-400"
                >
                  {title}
                </DialogTitle>
                <div className="mt-2">
                  <p className="text-sm text-gray-200">{message}</p>
                </div>
              </div>
            </div>
            <div className="mt-4 sm:flex sm:flex-row-reverse sm:gap-3">
              <button
                type="button"
                onClick={onConfirm}
                className={`inline-flex w-full justify-center rounded-md ${buttonBg} px-4 py-2 text-sm font-semibold text-gray-200 shadow-sm sm:w-auto transition-colors duration-200`}
              >
                {confirmText}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="inline-flex w-full justify-center rounded-md bg-gray-600 px-4 py-2 text-sm font-semibold text-gray-200 shadow-sm ring-1 ring-gray-500 hover:bg-gray-500 sm:w-auto transition-colors duration-200"
              >
                {cancelText}
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

const PieceWorkerLogTab = ({
  project,
  setProject,
  pieceworkerLogs,
  logsLoading,
  logsError,
  onViewLog,
  activeStatusTab,
  setActiveStatusTab,
}) => {
  const { id: projectId } = useParams();
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(
    location.state?.activeStatusTab || activeStatusTab || "Pending",
  );
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // State for creating a worksheet
  const [mode, setMode] = useState("history");
  const [stage, setStage] = useState("workOrder");
  const [workOrders, setWorkOrders] = useState([]);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [payPeriod, setPayPeriod] = useState(null);
  const [sundayDates, setSundayDates] = useState([]);
  const [lineItems, setLineItems] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [dialog, setDialog] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
    onConfirm: () => {},
  });

  const [bidAreaFilter, setBidAreaFilter] = useState("");
  const [bidAreas, setBidAreas] = useState([]);
  const [filteredLineItems, setFilteredLineItems] = useState([]);

  // Update filtered line items when bid area filter changes
  useEffect(() => {
    setFilteredLineItems(
      lineItems.filter(
        (item) => !bidAreaFilter || item.bidArea === bidAreaFilter,
      ),
    );
  }, [bidAreaFilter, lineItems]);

  useEffect(() => {
    if (setActiveStatusTab) {
      setActiveStatusTab(activeTab);
    }
  }, [activeTab, setActiveStatusTab]);

  // Generate Sundays for pay period selection
  useEffect(() => {
    const generateSundays = () => {
      const dates = [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dayOfWeek = today.getDay();
      const daysToSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;
      const currentSunday = new Date(today);
      if (dayOfWeek !== 0) {
        currentSunday.setDate(today.getDate() + daysToSunday);
      }

      for (let i = 0; i < 12; i++) {
        const sunday = new Date(currentSunday);
        sunday.setDate(currentSunday.getDate() - i * 7);
        dates.push({
          value: sunday.toISOString().split("T")[0],
          label: sunday.toLocaleDateString("en-US", {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
          }),
        });
      }

      setSundayDates(dates);
      setPayPeriod(dates[0]?.value || null);
    };
    generateSundays();
  }, []);

  // Fetch pieceworker logs for the project
  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("Authentication token not found");
        }

        const response = await axios.get(
          `https://erp-production-72da01c8e651.herokuapp.com/api/pieceworkerLogs?project=${projectId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setLogs(response.data);
        setFilteredLogs(
          response.data.filter((log) => log.status === "pending"),
        );
      } catch (err) {
        console.error("Error fetching pieceworker logs:", err);
        setError("Failed to load pieceworker logs.");
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, [projectId]);

  // Filter logs based on active tab
  useEffect(() => {
    const statusMap = {
      Pending: "pending",
      Approved: "approved",
      Rejected: "rejected",
    };
    setFilteredLogs(logs.filter((log) => log.status === statusMap[activeTab]));
  }, [activeTab, logs]);

  // Filter piecework work orders from project
  useEffect(() => {
    if (project && project.workOrders) {
      const pieceworkOrders = project.workOrders.filter(
        (wo) => wo.type === "piecework",
      );
      setWorkOrders(pieceworkOrders);
    }
  }, [project]);

  // Fetch employees when work order is selected
  const handleSelectWorkOrder = async (workOrder) => {
    setSelectedWorkOrder(workOrder);
    setLineItems(workOrder.lineItems || []);
    setFilteredLineItems(workOrder.lineItems || []);
    const uniqueBidAreas = [
      ...new Set(
        workOrder.lineItems
          .map((item) => item.bidArea)
          .filter((bidArea) => bidArea),
      ),
    ];
    setBidAreas(uniqueBidAreas);
    setBidAreaFilter("");
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `https://erp-production-72da01c8e651.herokuapp.com/api/users/pieceworkers`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setEmployees(response.data);
      setStage("employee");
    } catch (err) {
      console.error("Failed to load pieceworkers:", err);
      setCreateError("Failed to load pieceworkers.");
    }
  };

  const handleSelectEmployee = (employeeId) => {
    const employee = employees.find((emp) => emp._id === employeeId);
    setSelectedEmployee(employee);
    setStage("payPeriod");
  };

  const handlePayPeriodConfirm = () => {
    if (!payPeriod) {
      setCreateError("Please select a pay period.");
      return;
    }
    const initialQuantities = {};
    (selectedWorkOrder.lineItems || []).forEach((item) => {
      initialQuantities[item._id] = "";
    });
    setQuantities(initialQuantities);
    setStage("lineItems");
  };

  const handleQuantityChange = (itemId, value) => {
    const numValue = parseFloat(value) || 0;
    const item = lineItems.find((li) => li._id === itemId);
    const maxQty =
      (item.qtyEstimated || 0) -
      (item.qtyInstalled || 0) -
      (item.qtyPending || 0);
    if (numValue > maxQty) {
      setCreateError(`Quantity cannot exceed ${maxQty} ${item.uom}`);
      setQuantities((prev) => ({ ...prev, [itemId]: maxQty.toString() }));
    } else {
      setCreateError(null);
      setQuantities((prev) => ({ ...prev, [itemId]: value }));
    }
  };

  const handleSubmit = async () => {
    if (!selectedEmployee || Object.keys(quantities).length === 0) {
      setCreateError("Please select an employee and enter quantities.");
      return;
    }

    const validLineItems = lineItems
      .map((item) => ({
        lineItemId: item._id,
        title: item.title || "Untitled",
        quantity: parseFloat(quantities[item._id] || 0),
        uom: item.uom,
        unitCost: item.unitCost,
        bidArea: item.bidArea,
      }))
      .filter((item) => item.quantity > 0);

    if (validLineItems.length === 0) {
      setCreateError(
        "At least one line item with a quantity greater than 0 is required.",
      );
      return;
    }

    try {
      setCreateLoading(true);
      const token = localStorage.getItem("token");
      const pieceworkerLog = {
        projectId: projectId,
        workOrderId: selectedWorkOrder._id,
        employeeId: selectedEmployee._id,
        woNumber: selectedWorkOrder.woNumber || selectedWorkOrder._id,
        lineItems: validLineItems,
        workCompletedDate: payPeriod,
        status: "pending",
        submittedAt: new Date().toISOString(),
      };

      const response = await axios.post(
        `https://erp-production-72da01c8e651.herokuapp.com/api/pieceworkerLogs`,
        pieceworkerLog,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setLogs((prev) => [response.data.pieceworkerLog, ...prev]);
      setActiveTab("Pending");
      setMode("history");

      setDialog({
        open: true,
        type: "success",
        title: "Worksheet Submitted",
        message: "The pieceworker worksheet has been submitted for approval.",
        onConfirm: () => setDialog({ ...dialog, open: false }),
      });

      resetForm();
    } catch (err) {
      console.error("Error submitting worksheet:", err);
      setCreateError(
        err.response?.data?.message || "Failed to submit worksheet.",
      );
      setDialog({
        open: true,
        type: "error",
        title: "Submission Failed",
        message: err.response?.data?.message || "Failed to submit worksheet.",
        onConfirm: () => setDialog({ ...dialog, open: false }),
      });
    } finally {
      setCreateLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedWorkOrder(null);
    setEmployees([]);
    setSelectedEmployee(null);
    setLineItems([]);
    setQuantities({});
    setStage("workOrder");
    setPayPeriod(sundayDates[0]?.value || null);
    setCreateError(null);
  };

  const handleViewLog = (logId) => {
    if (onViewLog) {
      onViewLog(logId);
    } else {
      navigate(`/piecework-logs/${logId}`, {
        state: { fromProject: true, projectId, activeStatusTab: activeTab },
      });
    }
  };

  const getAvailabilityColor = (estimated, installed, pending) => {
    const totalUsed = (installed || 0) + (pending || 0);
    const available = (estimated || 0) - totalUsed;
    const percentage = estimated > 0 ? (available / estimated) * 100 : 0;

    if (percentage >= 60) {
      return { bg: "bg-green-400", text: "text-green-400", percentage };
    } else if (percentage >= 20) {
      return { bg: "bg-yellow-400", text: "text-yellow-400", percentage };
    } else {
      return { bg: "bg-red-400", text: "text-red-400", percentage };
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-700">
        <div className="text-gray-200 text-lg">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-700">
        <div className="text-red-400 text-lg">{error}</div>
      </div>
    );
  }

  const tabs = ["Pending", "Approved", "Rejected"];

  return (
    <div className="bg-gray-700 rounded-lg shadow-md border border-gray-600">
      <div className="mx-auto px-4 py-6">
        {mode === "history" && (
          <>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white mb-4 tracking-tight">
                Pieceworker Logs
              </h2>
              <button
                onClick={() => setMode("create")}
                className="inline-flex items-center px-4 py-2 bg-orange-400 text-gray-200 text-sm font-semibold rounded-md hover:bg-orange-500 transition-colors duration-200 shadow-sm"
              >
                Create New Worksheet
              </button>
            </div>
            <div className="border-b border-gray-600 mb-4">
              <nav className="flex space-x-4">
                {tabs.map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`py-2 px-4 text-sm font-medium rounded-t-md transition-colors duration-200 ${
                      activeTab === tab
                        ? "bg-gray-600 text-orange-400 border-b-2 border-orange-400"
                        : "text-gray-400 hover:text-gray-200 hover:bg-gray-600"
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </nav>
            </div>
            <div className="bg-gray-700 rounded-lg shadow-md border border-gray-600">
              <div className="p-6">
                <div className="overflow-hidden rounded-lg shadow-xl">
                  <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
                    <thead className="bg-gray-800">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider rounded-tl-lg">
                          Project
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Work Order
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          WO Number
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Employee
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Work Completed Date
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Total
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Subtotal
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wider rounded-tr-lg actions-column">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-600">
                      {filteredLogs.length > 0 ? (
                        filteredLogs.map((log, index) => {
                          // Sum the lineItems fields

                          const totalSum = log.lineItems
                            .reduce(
                              (sum, item) => sum + parseFloat(item.total || 0),
                              0,
                            )
                            .toFixed(2);
                          const burdenSum = log.lineItems
                            .reduce(
                              (sum, item) =>
                                sum + (item.pieceworkerBurdenCost || 0),
                              0,
                            )
                            .toFixed(2);
                          const wsibSum = log.lineItems
                            .reduce(
                              (sum, item) => sum + (item.wsibCost || 0),
                              0,
                            )
                            .toFixed(2);
                          const subtotalSum = log.lineItems
                            .reduce((sum, item) => {
                              const qty = parseFloat(item.qtyInstalled || 0);
                              if (qty <= 0) return sum;
                              const siteRate =
                                item.subtotal !== undefined && qty > 0
                                  ? Math.round((item.subtotal / qty) * 100) /
                                    100
                                  : parseFloat(item.unitCost || 0);
                              return (
                                sum + parseFloat((siteRate * qty).toFixed(2))
                              );
                            }, 0)
                            .toFixed(2);

                          return (
                            <tr
                              key={log._id}
                              onClick={(e) => {
                                if (e.target.closest(".actions-column")) return;
                                handleViewLog(log._id);
                              }}
                              className={`${
                                index % 2 === 0 ? "bg-gray-700" : "bg-gray-600"
                              } hover:bg-gray-500 cursor-pointer transition-colors duration-200`}
                            >
                              <td className="px-4 py-2 text-sm text-gray-200">
                                {log.project?.projectName || "N/A"}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-200">
                                {log.workOrder?.title || "N/A"}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-200">
                                {log.workOrder?.woNumber ||
                                  log.workOrder?._id ||
                                  "N/A"}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-200">
                                {log.user?.displayName ||
                                  log.user?.email ||
                                  "N/A"}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-200">
                                {log.workCompletedDate
                                  ? new Date(log.workCompletedDate)
                                      .toISOString()
                                      .split("T")[0]
                                  : "N/A"}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-200">
                                {totalSum !== "0.00" && totalSum !== "N/A"
                                  ? `$${Number(totalSum).toLocaleString(
                                      "en-US",
                                      {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      },
                                    )}`
                                  : "N/A"}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-200">
                                {subtotalSum !== "0.00" && subtotalSum !== "N/A"
                                  ? `$${Number(subtotalSum).toLocaleString(
                                      "en-US",
                                      {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      },
                                    )}`
                                  : "N/A"}
                              </td>
                              <td className="px-4 py-2 text-sm text-right actions-column">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewLog(log._id);
                                  }}
                                  className="text-orange-400 hover:text-orange-300 transition-colors duration-200"
                                >
                                  View
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td
                            colSpan="10"
                            className="px-4 py-4 text-center text-sm text-gray-400"
                          >
                            {logs.length === 0
                              ? "No pieceworker logs available."
                              : `No ${activeTab.toLowerCase()} logs found.`}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}

        {mode === "create" && (
          <div className="bg-gray-700 rounded-lg shadow-md border border-gray-600">
            <div className="p-6">
              {createError && (
                <div className="text-red-400 mb-4">{createError}</div>
              )}
              {createLoading && (
                <div className="text-center py-4 text-gray-200">
                  Submitting...
                </div>
              )}
              {stage === "workOrder" && (
                <>
                  <h2 className="text-lg font-semibold text-orange-400 mb-4">
                    Select Work Order
                  </h2>
                  {workOrders.length > 0 ? (
                    <div className="grid gap-4">
                      {workOrders.map((workOrder) => (
                        <div
                          key={workOrder._id}
                          className="flex items-center bg-gray-600 p-3 rounded-md cursor-pointer hover:bg-gray-500 transition-colors duration-200"
                          onClick={() => handleSelectWorkOrder(workOrder)}
                        >
                          <div className="w-10 h-10 bg-gray-500 rounded-full flex items-center justify-center mr-3">
                            <svg
                              className="w-5 h-5 text-gray-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                              />
                            </svg>
                          </div>
                          <span className="text-sm text-gray-200">
                            {workOrder.title}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400">
                      No piecework work orders available.
                    </p>
                  )}
                  <button
                    onClick={() => setMode("history")}
                    className="mt-4 inline-flex items-center px-4 py-2 bg-gray-600 text-gray-200 text-sm font-semibold rounded-md hover:bg-gray-500 transition-colors duration-200"
                  >
                    <ArrowLeftIcon className="size-4 mr-2" />
                    Back to Logs
                  </button>
                </>
              )}
              {stage === "employee" && (
                <>
                  <h2 className="text-lg font-semibold text-orange-400 mb-4">
                    Select Pieceworker
                  </h2>
                  <select
                    value={selectedEmployee?._id || ""}
                    onChange={(e) => handleSelectEmployee(e.target.value)}
                    className="block w-full max-w-md text-sm text-gray-200 bg-gray-600 border border-gray-500 rounded-md p-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="" disabled>
                      Select an employee
                    </option>
                    {employees.map((employee) => (
                      <option key={employee._id} value={employee._id}>
                        {employee.displayName || employee.email}
                      </option>
                    ))}
                  </select>
                  <div className="flex space-x-4 mt-4">
                    <button
                      onClick={() => setStage("workOrder")}
                      className="inline-flex items-center px-4 py-2 bg-gray-600 text-gray-200 text-sm font-semibold rounded-md hover:bg-gray-500 transition-colors duration-200"
                    >
                      <ArrowLeftIcon className="size-4 mr-2" />
                      Back
                    </button>
                  </div>
                </>
              )}
              {stage === "payPeriod" && (
                <>
                  <h2 className="text-lg font-semibold text-orange-400 mb-4">
                    Select Pay Period for{" "}
                    {selectedEmployee?.displayName || selectedEmployee?.email}
                  </h2>
                  <div className="relative mb-4 max-w-md">
                    <button
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className="w-full bg-gray-600 border border-gray-500 rounded-md p-2 text-left text-sm text-gray-200 focus:ring-orange-500 focus:border-orange-500"
                    >
                      {sundayDates.find((d) => d.value === payPeriod)?.label ||
                        "Select a pay period"}
                      <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400">
                        {isDropdownOpen ? "▲" : "▼"}
                      </span>
                    </button>
                    {isDropdownOpen && (
                      <div className="absolute w-full bg-gray-700 border border-gray-600 rounded-md mt-1 max-h-40 overflow-y-auto shadow-lg z-10">
                        {sundayDates.map((date) => (
                          <div
                            key={date.value}
                            onClick={() => {
                              setPayPeriod(date.value);
                              setIsDropdownOpen(false);
                            }}
                            className="p-2 hover:bg-gray-600 cursor-pointer text-sm text-gray-200"
                          >
                            {date.label}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex space-x-4">
                    <button
                      onClick={handlePayPeriodConfirm}
                      className="inline-flex items-center px-4 py-2 bg-orange-400 text-gray-200 text-sm font-semibold rounded-md hover:bg-orange-500 transition-colors duration-200"
                    >
                      Confirm Pay Period
                    </button>
                    <button
                      onClick={() => setStage("employee")}
                      className="inline-flex items-center px-4 py-2 bg-gray-600 text-gray-200 text-sm font-semibold rounded-md hover:bg-gray-500 transition-colors duration-200"
                    >
                      <ArrowLeftIcon className="size-4 mr-2" />
                      Back
                    </button>
                  </div>
                </>
              )}
              {stage === "lineItems" && (
                <>
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold text-white">
                      Report Quantities for{" "}
                      {selectedEmployee?.displayName || selectedEmployee?.email}{" "}
                      - Pay Period Ending{" "}
                      {sundayDates.find((d) => d.value === payPeriod)?.label ||
                        payPeriod}
                    </h2>
                    <div>
                      <select
                        value={bidAreaFilter}
                        onChange={(e) => setBidAreaFilter(e.target.value)}
                        className="block text-sm text-gray-200 bg-gray-600 border border-gray-500 rounded-md p-2 focus:ring-orange-500 focus:border-orange-500"
                      >
                        <option value="">All Bid Areas</option>
                        {bidAreas.map((bidArea) => (
                          <option key={bidArea} value={bidArea}>
                            {bidArea}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {filteredLineItems.map((item) => {
                    const remainingQty =
                      (item.qtyEstimated || 0) -
                      (item.qtyInstalled || 0) -
                      (item.qtyPending || 0);
                    const { bg, text, percentage } = getAvailabilityColor(
                      item.qtyEstimated,
                      item.qtyInstalled,
                      item.qtyPending,
                    );
                    return (
                      <div
                        key={item._id}
                        className="bg-gray-600 p-4 mb-4 rounded-md"
                      >
                        <h3 className="text-gray-200 font-medium mb-2 text-sm">
                          {item.title || "Item"}
                        </h3>
                        <p className="text-sm text-gray-200 mb-2">
                          Bid Area: {item.bidArea || "N/A"}
                        </p>
                        <div className="grid grid-cols-2 gap-2 mb-2 text-sm">
                          <p className="text-gray-200">
                            Est: {(item.qtyEstimated || 0).toLocaleString()}{" "}
                            {item.uom}
                          </p>
                          <p className="text-gray-200">
                            Inst: {(item.qtyInstalled || 0).toLocaleString()}{" "}
                            {item.uom}
                          </p>
                          <p className="text-gray-200">
                            Pend: {(item.qtyPending || 0).toLocaleString()}{" "}
                            {item.uom}
                          </p>
                          <p className={text}>
                            Avail: {remainingQty.toLocaleString()} {item.uom} (
                            {percentage.toFixed(1)}%)
                          </p>
                        </div>
                        <div className="w-full bg-gray-600 rounded-full h-1.5 mb-3">
                          <div
                            className={`${bg} h-1.5 rounded-full`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        <div className="flex items-center mb-2">
                          <input
                            type="number"
                            value={quantities[item._id] || ""}
                            onChange={(e) =>
                              handleQuantityChange(item._id, e.target.value)
                            }
                            placeholder="Qty"
                            className="bg-gray-600 border border-gray-500 rounded-md p-2 w-20 text-sm text-gray-200 focus:ring-orange-500 focus:border-orange-500"
                          />
                          <span className="ml-2 text-sm text-gray-400">
                            {item.uom}
                          </span>
                        </div>
                        <p className="text-sm text-gray-200">
                          Unit Cost: ${item.unitCost.toFixed(2)} | Total: $
                          {(
                            (quantities[item._id] || 0) * item.unitCost
                          ).toFixed(2)}
                        </p>
                      </div>
                    );
                  })}
                  <div className="flex space-x-4">
                    <button
                      onClick={handleSubmit}
                      disabled={createLoading}
                      className="inline-flex items-center px-4 py-2 bg-orange-400 text-gray-200 text-sm font-semibold rounded-md hover:bg-orange-500 disabled:bg-gray-600 disabled:opacity-50 transition-colors duration-200"
                    >
                      Submit Worksheet
                    </button>
                    <button
                      onClick={() => setStage("payPeriod")}
                      className="inline-flex items-center px-4 py-2 bg-gray-600 text-gray-200 text-sm font-semibold rounded-md hover:bg-gray-500 transition-colors duration-200"
                    >
                      <ArrowLeftIcon className="size-4 mr-2" />
                      Back
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        <AlertDialog
          open={dialog.open}
          type={dialog.type}
          title={dialog.title}
          message={dialog.message}
          confirmText="OK"
          cancelText="Close"
          onClose={() => setDialog({ ...dialog, open: false })}
          onConfirm={dialog.onConfirm}
        />
      </div>
    </div>
  );
};

export default PieceWorkerLogTab;
